import React, { useMemo, useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './AdminSMMManager.css';

const toNumberOrEmpty = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n : '';
};

const formatUsdt = (weiStr, decimals = 18, places = 2) => {
  try {
    const s = String(weiStr || '0');
    const big = BigInt(s);
    const base = 10n ** BigInt(decimals);
    const whole = big / base;
    const frac = (big % base).toString().padStart(decimals, '0').slice(0, Math.min(decimals, places));
    return `${whole.toString()}.${frac.padEnd(places, '0')}`;
  } catch (_) {
    return '0.00';
  }
};

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toFixed(2);
};

const normalizeSellRate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeDescription = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

// White-labeled platform grouping for admin convenience
const getPlatformFromService = (s) => {
  const hay = `${s?.category || ''} ${s?.name || ''}`.toLowerCase();
  if (hay.includes('instagram')) return 'Instagram';
  if (hay.includes('youtube')) return 'YouTube';
  if (hay.includes('tiktok') || hay.includes('tik tok')) return 'TikTok';
  if (hay.includes('spotify')) return 'Spotify';
  if (hay.includes('telegram')) return 'Telegram';
  if (hay.includes('twitter') || hay.includes('x ')) return 'Twitter';
  if (hay.includes('facebook')) return 'Facebook';
  if (hay.includes('reddit')) return 'Reddit';
  if (hay.includes('twitch')) return 'Twitch';
  if (hay.includes('seo') || hay.includes('backlink')) return 'SEO Backlinks';
  if (hay.includes('traffic') || hay.includes('website')) return 'Website Traffic';
  return 'Other';
};

const AdminSMMManager = () => {
  const [activeTab, setActiveTab] = useState('services'); // services | orders
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLimit] = useState(50);
  // Server-side pagination for orders. Previously the UI only ever fetched the
  // most-recent 100 orders and "paged" client-side over that slice — every
  // older order was invisible. These mirror the server's pagination object.
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [savingIds, setSavingIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [search, setSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [platform, setPlatform] = useState('All');
  const [statusFilter, setStatusFilter] = useState('all');
  const [svcPage, setSvcPage] = useState(1);
  const [svcLimit, setSvcLimit] = useState(50);
  const [svcTotalPages, setSvcTotalPages] = useState(1);
  const [providers, setProviders] = useState([]); // [{key,label,enabled}]
  const [provider, setProvider] = useState('smmflare'); // which provider's catalog to manage
  const [servicesError, setServicesError] = useState('');
  const [ordersError, setOrdersError] = useState('');
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const selectAllRef = useRef(null);
  const servicesSnapshotRef = useRef(new Map());

  const getPageItems = (current, total) => {
    if (total <= 1) return [1];
    const items = [];
    const windowSize = 2;
    const start = Math.max(2, current - windowSize);
    const end = Math.min(total - 1, current + windowSize);

    items.push(1);
    if (start > 2) items.push('ellipsis-left');
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
    if (end < total - 1) items.push('ellipsis-right');
    items.push(total);
    return items;
  };

  useEffect(() => {
    fetchOrders();
    fetchServicesPage(1, search, platform, statusFilter);
    requestWithTestFallback('/smm/admin/providers', { method: 'GET' })
      .then((r) => setProviders(r?.data?.providers || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Let the Services Overview deep-link into a specific provider's catalog.
  useEffect(() => {
    const onSelectProvider = (e) => {
      const key = e?.detail?.provider;
      if (key) { setActiveTab('services'); setProvider(key); }
    };
    window.addEventListener('smm:selectProvider', onSelectProvider);
    return () => window.removeEventListener('smm:selectProvider', onSelectProvider);
  }, []);

  // Re-fetch the catalog when the admin switches providers.
  const providerInitRef = useRef(false);
  useEffect(() => {
    if (!providerInitRef.current) { providerInitRef.current = true; return; }
    setSvcPage(1);
    fetchServicesPage(1, search, platform, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const requestWithTestFallback = async (primaryUrl, opts = {}) => {
    try {
      return await api.request({ url: primaryUrl, ...opts });
    } catch (e) {
      const status = e?.response?.status;
      // In dev, admin pages often run in "test mode" using x-wallet-address header.
      // Fall back to /api/test routes if JWT auth is missing/expired.
      const method = String(opts?.method || 'GET').toUpperCase();
      // Allow fallback for GET and PUT (for bulk operations)
      const allowFallback = method === 'GET' || method === 'PUT' || method === 'POST';
      if (allowFallback && (status === 401 || status === 403 || status === 404)) {
        const testUrl = primaryUrl.startsWith('/smm/') ? `/test${primaryUrl}` : `/test${primaryUrl}`;
        return await api.request({ url: testUrl, ...opts });
      }
      throw e;
    }
  };

  // Search is now done server-side (see fetchOrders + the debounced effect on
  // orderSearch), so a wallet address can find ALL of its historical orders —
  // not just hits that happen to be on the visible page. No client-side filter.
  const filteredOrders = orders;

  const fetchOrders = async (page = ordersPage, opts = {}) => {
    try {
      setLoading(true);
      setOrdersError('');
      // Default to refresh=false so paging through history is fast. The
      // dedicated "Refresh all" button still pulls live provider statuses.
      const refresh = opts.refresh === true;
      const params = new URLSearchParams({
        page: String(Math.max(1, Number(page) || 1)),
        limit: String(ordersLimit),
        refresh: String(refresh)
      });
      const searchQ = String(opts.search ?? orderSearch ?? '').trim();
      if (searchQ) params.set('search', searchQ);
      const ordRes = await requestWithTestFallback(`/smm/admin/orders?${params}`, { method: 'GET' });
      setOrders(ordRes.data?.orders || []);
      setOrdersTotalPages(Math.max(1, Number(ordRes.data?.pagination?.pages) || 1));
      setOrdersTotal(Number(ordRes.data?.pagination?.total) || 0);
      setOrdersPage(Number(ordRes.data?.pagination?.page) || page);
    } catch (e) {
      console.error('Admin SMM load error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load orders';
      setOrdersError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Debounced server-side search: refetches page 1 whenever the query changes.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchOrders(1, { search: orderSearch });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSearch]);

  const fetchServicesPage = async (page, q, plat, status, limitOverride) => {
    try {
      setLoading(true);
      setServicesError('');
      const effectiveLimit = Number.isFinite(Number(limitOverride ?? svcLimit))
        ? Number(limitOverride ?? svcLimit)
        : 50;
      const requestedPage = Math.max(1, Number(page) || 1);

      // For active/inactive view, build pages from a fully filtered dataset so each page is dense.
      // This avoids sparse pages when backend filtering is stale or unavailable in a running instance.
      if (status === 'active' || status === 'inactive') {
        const all = [];
        let current = 1;
        let totalPages = 1;
        const serverPageLimit = 500;
        const maxPageFetches = 200;

        do {
          const params = new URLSearchParams({
            page: String(current),
            limit: String(serverPageLimit),
            search: String(q || ''),
            platform: String(plat || 'All'),
            provider: String(provider || 'smmflare')
          });
          params.set('enabled', status === 'active' ? 'true' : 'false');
          const resp = await requestWithTestFallback(`/smm/admin/services?${params}`, { method: 'GET' });
          const chunk = Array.isArray(resp?.data?.services) ? resp.data.services : [];
          all.push(...chunk);
          totalPages = Math.max(1, Number(resp?.data?.pagination?.pages) || 1);
          current += 1;
        } while (current <= totalPages && current <= maxPageFetches);

        // Safety: if server ignored enabled filter, enforce status locally.
        const statusScoped = status === 'active'
          ? all.filter((s) => Boolean(s.enabled))
          : all.filter((s) => !Boolean(s.enabled));

        const localPages = Math.max(1, Math.ceil(statusScoped.length / effectiveLimit));
        const safePage = Math.min(requestedPage, localPages);
        const start = (safePage - 1) * effectiveLimit;
        const pageItems = statusScoped.slice(start, start + effectiveLimit);

        setServices(pageItems);
        servicesSnapshotRef.current = new Map(
          pageItems.map((s) => [
            Number(s.serviceId ?? s.service),
            {
              enabled: Boolean(s.enabled),
              sellRatePer1kUsd: normalizeSellRate(s.sellRatePer1kUsd),
              description: normalizeDescription(s.description),
              descriptionEnabled: Boolean(s.descriptionEnabled)
            }
          ])
        );
        setSvcPage(safePage);
        setSvcTotalPages(localPages);
      } else {
        const params = new URLSearchParams({
          page: String(requestedPage),
          limit: String(effectiveLimit),
          search: String(q || ''),
          platform: String(plat || 'All'),
          provider: String(provider || 'smmflare')
        });
        const svcRes = await requestWithTestFallback(`/smm/admin/services?${params}`, { method: 'GET' });
        const pageItems = Array.isArray(svcRes?.data?.services) ? svcRes.data.services : [];
        setServices(pageItems);
        servicesSnapshotRef.current = new Map(
          pageItems.map((s) => [
            Number(s.serviceId ?? s.service),
            {
              enabled: Boolean(s.enabled),
              sellRatePer1kUsd: normalizeSellRate(s.sellRatePer1kUsd),
              description: normalizeDescription(s.description),
              descriptionEnabled: Boolean(s.descriptionEnabled)
            }
          ])
        );
        setSvcPage(Number(svcRes?.data?.pagination?.page) || requestedPage);
        setSvcTotalPages(Math.max(1, Number(svcRes?.data?.pagination?.pages) || 1));
      }
    } catch (e) {
      console.error('Admin SMM services load error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load services';
      setServicesError(msg);
      toast.error(msg);
      setServices([]);
      setSvcTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search/platform/status/limit -> paginated fetch
  useEffect(() => {
    const t = setTimeout(() => {
      fetchServicesPage(1, search, platform, statusFilter);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, platform, statusFilter, svcLimit]);

  const platforms = useMemo(() => {
    const base = ['All', 'Spotify', 'Instagram', 'YouTube', 'TikTok', 'Telegram', 'Twitter', 'Facebook', 'Reddit', 'Twitch', 'Website Traffic', 'SEO Backlinks', 'Other'];
    return base;
  }, []);

  // Server already applies search/platform before pagination.
  // Keep a status-only guard client-side to avoid any inconsistent rows.
  const filteredServices = useMemo(() => {
    if (statusFilter === 'active') return services.filter((s) => Boolean(s.enabled));
    if (statusFilter === 'inactive') return services.filter((s) => !Boolean(s.enabled));
    return services;
  }, [services, statusFilter]);

  const pageAllEnabled = useMemo(() => {
    if (!filteredServices.length) return false;
    return filteredServices.every((s) => Boolean(s.enabled));
  }, [filteredServices]);

  const pageSomeEnabled = useMemo(() => {
    if (!filteredServices.length) return false;
    return filteredServices.some((s) => Boolean(s.enabled));
  }, [filteredServices]);

  const dirtyServicesForPage = useMemo(() => {
    if (!filteredServices.length) return [];
    const snapshot = servicesSnapshotRef.current;
    return filteredServices.filter((s) => {
      const serviceId = Number(s.serviceId ?? s.service);
      if (!Number.isFinite(serviceId)) return false;
      const snap = snapshot.get(serviceId);
      if (!snap) return true;
      const enabled = Boolean(s.enabled);
      const sellRate = normalizeSellRate(s.sellRatePer1kUsd);
      const desc = normalizeDescription(s.description);
      const descEnabled = Boolean(s.descriptionEnabled);
      return (
        snap.enabled !== enabled ||
        snap.sellRatePer1kUsd !== sellRate ||
        snap.description !== desc ||
        snap.descriptionEnabled !== descEnabled
      );
    });
  }, [filteredServices]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = pageSomeEnabled && !pageAllEnabled;
  }, [pageAllEnabled, pageSomeEnabled]);

  const updateLocalService = (serviceId, patch) => {
    setServices((prev) =>
      prev.map((s) => (Number(s.serviceId) === Number(serviceId) ? { ...s, ...patch } : s))
    );
  };

  const buildServiceDeltaPayload = (service, snapshot) => {
    const payload = {};
    const enabled = Boolean(service?.enabled);
    const sellRate = normalizeSellRate(service?.sellRatePer1kUsd);
    const desc = normalizeDescription(service?.description);
    const descEnabled = Boolean(service?.descriptionEnabled);

    if (!snapshot || snapshot.enabled !== enabled) {
      payload.enabled = enabled;
    }
    if (!snapshot || snapshot.sellRatePer1kUsd !== sellRate) {
      payload.sellRatePer1kUsd = sellRate;
    }
    if (!snapshot || snapshot.description !== desc) {
      payload.description = desc;
    }
    if (!snapshot || snapshot.descriptionEnabled !== descEnabled) {
      payload.descriptionEnabled = descEnabled;
    }
    return payload;
  };

  const saveService = async (service) => {
    const serviceId = Number(service.serviceId ?? service.service);
    if (!Number.isFinite(serviceId)) return;
    try {
      setSavingIds((prev) => new Set(prev).add(serviceId));
      const snapshot = servicesSnapshotRef.current.get(serviceId);
      const payload = buildServiceDeltaPayload(service, snapshot);
      if (Object.keys(payload).length === 0) {
        toast.info(`No changes for service #${serviceId}.`);
        return;
      }
      payload.provider = service.provider || provider || 'smmflare';
      await requestWithTestFallback(`/smm/admin/services/${serviceId}`, { method: 'PUT', data: payload });
      toast.success(`Saved service #${serviceId}`);
      // Refresh current page to show updated effective rate
      await fetchServicesPage(svcPage, search, platform, statusFilter);
    } catch (e) {
      console.error('Save service error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to update service';
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(detail ? `${msg} (${detail})` : status ? `${msg} (HTTP ${status})` : msg);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(serviceId);
        return next;
      });
    }
  };

  const bulkSetEnabledForPage = async (enabled) => {
    if (bulkSaving) return;
    if (!filteredServices.length) return;
    if (!window.confirm(`${enabled ? 'Enable' : 'Disable'} all ${filteredServices.length} services on this page?`)) return;

    try {
      setBulkSaving(true);
      setBulkProgress({ done: 0, total: filteredServices.length });

      // Optimistic UI update for this page
      setServices((prev) =>
        prev.map((s) => {
          const sid = Number(s.serviceId ?? s.service);
          const inPage = filteredServices.some((x) => Number(x.serviceId ?? x.service) === sid);
          return inPage ? { ...s, enabled } : s;
        })
      );

      // Use bulk endpoint (ONE request) instead of 200 individual requests
      const serviceIds = filteredServices
        .map((s) => Number(s.serviceId ?? s.service))
        .filter((n) => Number.isFinite(n));

      if (serviceIds.length === 0) {
        toast.warn('No valid services to update.');
        return;
      }

      try {
        await requestWithTestFallback('/smm/admin/services/bulk', {
          method: 'PUT',
          data: { serviceIds, enabled, provider: provider || 'smmflare' }
        });
        toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${serviceIds.length} services.`);
      } catch (e) {
        console.error('Bulk update error:', e);
        const msg = e?.response?.data?.message || e?.message || 'Failed to update services';
        toast.error(msg);
      }

      setBulkProgress({ done: filteredServices.length, total: filteredServices.length });
      await fetchServicesPage(svcPage, search, platform, statusFilter);
    } finally {
      setBulkSaving(false);
      setBulkProgress({ done: 0, total: 0 });
    }
  };

  const bulkSaveChangesForPage = async () => {
    if (bulkSaving) return;
    if (!dirtyServicesForPage.length) {
      toast.info('No changes to save on this page.');
      return;
    }
    if (!window.confirm(`Save ${dirtyServicesForPage.length} service${dirtyServicesForPage.length === 1 ? '' : 's'} on this page?`)) {
      return;
    }

    try {
      setBulkSaving(true);
      setBulkProgress({ done: 0, total: dirtyServicesForPage.length });

      const servicesPayload = dirtyServicesForPage
        .map((s) => {
          const serviceId = Number(s.serviceId ?? s.service);
          if (!Number.isFinite(serviceId)) return null;
          const snapshot = servicesSnapshotRef.current.get(serviceId);
          const delta = buildServiceDeltaPayload(s, snapshot);
          if (Object.keys(delta).length === 0) return null;
          return { serviceId, provider: s.provider || provider || 'smmflare', ...delta };
        })
        .filter(Boolean);

      if (servicesPayload.length === 0) {
        toast.warn('No valid services to update.');
        return;
      }

      await requestWithTestFallback('/smm/admin/services/bulk', {
        method: 'PUT',
        data: { services: servicesPayload }
      });
      toast.success(`Saved ${servicesPayload.length} services.`);
      setBulkProgress({ done: dirtyServicesForPage.length, total: dirtyServicesForPage.length });
      await fetchServicesPage(svcPage, search, platform, statusFilter);
    } catch (e) {
      console.error('Bulk save error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to save services';
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(detail ? `${msg} (${detail})` : status ? `${msg} (HTTP ${status})` : msg);
    } finally {
      setBulkSaving(false);
      setBulkProgress({ done: 0, total: 0 });
    }
  };

  const refreshOrderStatuses = async (silent = false, baseCount = orders.length) => {
    if (refreshingStatuses) return;
    try {
      setRefreshingStatuses(true);
      setOrdersError('');
      const res = await requestWithTestFallback('/smm/admin/orders/refresh-status', {
        method: 'POST',
        data: { limit: Math.max(baseCount, ordersLimit) }
      });
      if (res?.data?.orders) {
        setOrders((prev) => {
          const prevById = new Map(prev.map((o) => [String(o._id || o.orderId), o]));
          return res.data.orders.map((o) => {
            const key = String(o._id || o.orderId);
            const old = prevById.get(key);
            return {
              ...o,
              remains: o?.remains != null ? o.remains : old?.remains
            };
          });
        });
      }
      if (!silent) {
        const updated = Number(res?.data?.updated || 0);
        toast.success(`Updated ${updated} order status${updated === 1 ? '' : 'es'}.`);
      }
    } catch (e) {
      console.error('Refresh statuses error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to refresh order statuses';
      setOrdersError(msg);
      if (!silent) {
        toast.error(msg);
      }
    } finally {
      setRefreshingStatuses(false);
    }
  };

  if (loading) {
    return <div className="admin-smm-card">Loading SMM admin…</div>;
  }

  // The server now returns a single page — render it directly. The orderSearch
  // box still filters the CURRENT page client-side (cheap), so users can scope
  // a visible page; for matches in older pages they navigate via the pager.
  const pagedOrders = filteredOrders;

  return (
    <div className="admin-smm">
      <div className="admin-smm-header">
        <div className="admin-smm-title">SMM</div>
        <div className="admin-smm-actions">
          <button
            className="admin-smm-btn"
            onClick={() => {
              fetchOrders();
              fetchServicesPage(svcPage, search, platform, statusFilter);
            }}
            type="button"
          >
            Refresh
          </button>
          <button
            className="admin-smm-btn admin-smm-btn--ghost"
            onClick={refreshOrderStatuses}
            type="button"
            disabled={refreshingStatuses}
            title="Pull latest status from provider"
          >
            {refreshingStatuses ? 'Refreshing…' : 'Refresh all'}
          </button>
        </div>
      </div>

      <div className="admin-smm-tabs">
        <button className={`admin-smm-tab ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>Services</button>
        <button className={`admin-smm-tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>Orders</button>
      </div>

      {activeTab === 'services' && (
        <div className="admin-smm-card">
          <div className="admin-smm-filters">
            <select
              className="admin-smm-select"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              title="Which provider's catalog to manage"
            >
              {(providers.length ? providers : [{ key: 'smmflare', label: 'Provider A', enabled: true }]).map((p) => (
                <option key={p.key} value={p.key} disabled={!p.enabled}>
                  {p.label}{p.enabled ? '' : ' (no key)'}
                </option>
              ))}
            </select>
            <input
              className="admin-smm-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search service id/name/category…"
            />
            <select className="admin-smm-select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {platforms.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              className="admin-smm-select"
              value={statusFilter}
              onChange={(e) => {
                const nextStatus = e.target.value;
                setStatusFilter(nextStatus);
                setSvcPage(1);
                fetchServicesPage(1, search, platform, nextStatus);
              }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
            <select
              className="admin-smm-select"
              value={String(svcLimit)}
              onChange={(e) => {
                const nextLimit = Number(e.target.value);
                setSvcLimit(nextLimit);
                setSvcPage(1);
                fetchServicesPage(1, search, platform, statusFilter, nextLimit);
              }}
            >
              <option value="10">10 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
            <button
              className="admin-smm-btn admin-smm-btn--ghost"
              type="button"
              disabled={bulkSaving || filteredServices.length === 0}
              onClick={() => bulkSetEnabledForPage(true)}
              title="Enable all services on this page"
            >
              {bulkSaving ? `Saving ${bulkProgress.done}/${bulkProgress.total}…` : 'Enable all (page)'}
            </button>
            <button
              className="admin-smm-btn admin-smm-btn--ghost"
              type="button"
              disabled={bulkSaving || filteredServices.length === 0}
              onClick={() => bulkSetEnabledForPage(false)}
              title="Disable all services on this page"
            >
              {bulkSaving ? `Saving ${bulkProgress.done}/${bulkProgress.total}…` : 'Disable all (page)'}
            </button>
            <button
              className="admin-smm-btn"
              type="button"
              disabled={bulkSaving || dirtyServicesForPage.length === 0}
              onClick={bulkSaveChangesForPage}
              title="Save all changes on this page"
            >
              {bulkSaving ? `Saving ${bulkProgress.done}/${bulkProgress.total}…` : `Save all (page)`}
            </button>
          </div>

          {servicesError && (
            <div className="admin-smm-error">
              {servicesError}
            </div>
          )}
          <div style={{ marginBottom: 8, color: 'var(--text-secondary)', fontSize: 12 }}>
            Showing {filteredServices.length} services on this page.
          </div>

          <div className="admin-smm-pager">
            <button
              className="admin-smm-btn admin-smm-btn--ghost"
              type="button"
              disabled={svcPage <= 1}
              onClick={() => fetchServicesPage(Math.max(1, svcPage - 1), search, platform, statusFilter)}
            >
              Prev
            </button>
            <div className="admin-smm-pagernumbers">
              {getPageItems(svcPage, svcTotalPages).map((item) => {
                if (typeof item !== 'number') {
                  return (
                    <span key={item} className="admin-smm-ellipsis">…</span>
                  );
                }
                const isActive = item === svcPage;
                return (
                  <button
                    key={item}
                    className={`admin-smm-pagebtn ${isActive ? 'active' : ''}`}
                    type="button"
                    disabled={isActive}
                    onClick={() => fetchServicesPage(item, search, platform, statusFilter)}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
            <button
              className="admin-smm-btn admin-smm-btn--ghost"
              type="button"
              disabled={svcPage >= svcTotalPages}
              onClick={() => fetchServicesPage(Math.min(svcTotalPages, svcPage + 1), search, platform, statusFilter)}
            >
              Next
            </button>
          </div>

          <div className="admin-smm-tablewrap">
            <table className="data-table admin-smm-table">
              <thead>
                <tr>
                  <th>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={pageAllEnabled}
                        disabled={bulkSaving || filteredServices.length === 0}
                        onChange={(e) => bulkSetEnabledForPage(e.target.checked)}
                        aria-label="Enable/disable all services on this page"
                      />
                      <span>Enabled</span>
                    </div>
                  </th>
                  <th>Provider #</th>
                  <th>Internal # (user sees)</th>
                  <th>Platform</th>
                  <th>Category</th>
                  <th>Name</th>
                  <th>Provider rate /1k</th>
                  <th>Your rate /1k</th>
                  <th>Effective /1k</th>
                  <th>Description</th>
                  <th>Show</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((s) => {
                  const serviceId = Number(s.serviceId ?? s.service);
                  const isSaving = savingIds.has(serviceId);
                  return (
                    <tr key={serviceId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(s.enabled)}
                          onChange={(e) => updateLocalService(serviceId, { enabled: e.target.checked })}
                        />
                      </td>
                      <td><code>#{serviceId}</code></td>
                      <td><strong>{s.internalServiceId || '—'}</strong></td>
                      <td>{s.platform || getPlatformFromService(s)}</td>
                      <td>{s.category}</td>
                      <td style={{ maxWidth: 520 }}>{s.name}</td>
                      <td>{toNumberOrEmpty(s.providerRatePer1kUsd) ?? '—'}</td>
                      <td>
                        <input
                          className="admin-smm-rate"
                          type="number"
                          min="0"
                          step="0.01"
                          value={s.sellRatePer1kUsd === null ? '' : String(s.sellRatePer1kUsd)}
                          placeholder="(use provider)"
                          onChange={(e) => updateLocalService(serviceId, { sellRatePer1kUsd: e.target.value === '' ? null : e.target.value })}
                        />
                      </td>
                      <td>{toNumberOrEmpty(s.effectiveRatePer1kUsd) ?? '—'}</td>
                      <td>
                        <textarea
                          className="admin-smm-desc"
                          rows={3}
                          placeholder={s.providerDescription || 'No provider description'}
                          value={s.description || ''}
                          onChange={(e) => updateLocalService(serviceId, { description: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(s.descriptionEnabled)}
                          onChange={(e) => updateLocalService(serviceId, { descriptionEnabled: e.target.checked })}
                          aria-label={`Show description for service #${serviceId}`}
                        />
                      </td>
                      <td>
                        <button className="admin-smm-btn" disabled={isSaving} onClick={() => saveService(s)}>
                          {isSaving ? 'Saving…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredServices.length === 0 && (
                  <tr>
                    <td colSpan="11" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)' }}>
                      No services found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {svcTotalPages > 1 && (
            <div className="admin-smm-pager">
              <button
                className="admin-smm-btn admin-smm-btn--ghost"
                type="button"
                disabled={svcPage <= 1}
                onClick={() => fetchServicesPage(Math.max(1, svcPage - 1), search, platform, statusFilter)}
              >
                Prev
              </button>
              <div className="admin-smm-pagernumbers">
                {getPageItems(svcPage, svcTotalPages).map((item) => {
                  if (typeof item !== 'number') {
                    return (
                      <span key={item} className="admin-smm-ellipsis">…</span>
                    );
                  }
                  const isActive = item === svcPage;
                  return (
                    <button
                      key={item}
                      className={`admin-smm-pagebtn ${isActive ? 'active' : ''}`}
                      type="button"
                      disabled={isActive}
                      onClick={() => fetchServicesPage(item, search, platform, statusFilter)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
              <button
                className="admin-smm-btn admin-smm-btn--ghost"
                type="button"
                disabled={svcPage >= svcTotalPages}
                onClick={() => fetchServicesPage(Math.min(svcTotalPages, svcPage + 1), search, platform, statusFilter)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="admin-smm-card">
          {ordersError && (
            <div className="admin-smm-error">
              {ordersError}
            </div>
          )}
          <div className="admin-smm-filters">
            <input
              className="admin-smm-input"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search ALL orders by wallet (0x…) or provider order ID..."
            />
          </div>
          <div className="admin-smm-tablewrap">
            <table className="data-table admin-smm-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Wallet</th>
                  <th>Service</th>
                  <th>Qty</th>
                  <th>Status</th>
                  <th>Remains</th>
                  <th>User paid (USDT)</th>
                  <th>You charged (USDT)</th>
                  <th>Provider cost</th>
                  <th>Internal #</th>
                  <th>Provider #</th>
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map((o) => (
                  <tr key={o._id || `${o.walletAddress}-${o.orderId}`}>
                    <td>{o.createdAt ? new Date(o.createdAt).toLocaleString() : '—'}</td>
                    <td><code>{o.walletAddress}</code></td>
                    <td>
                      <div style={{ fontWeight: 800 }}>{o.serviceName}</div>
                      <div style={{ opacity: 0.8 }}>#{o.serviceId}</div>
                    </td>
                    <td>{o.quantity}</td>
                    <td>{o.status || 'Pending'}</td>
                    <td>{o?.remains != null ? o.remains : '—'}</td>
                    <td>{formatUsdt(o.escrowAmount, 18, 2)}</td>
                    <td>{o.chargedUsd != null ? formatMoney(o.chargedUsd) : '—'}</td>
                    <td>
                      {o.charge > 0
                        ? `${formatMoney(o.charge)} ${o.currency || 'USD'}`
                        : (o.providerRatePer1kUsd != null && o.quantity
                          ? `${formatMoney((Number(o.providerRatePer1kUsd) * Number(o.quantity)) / 1000)} USD (est)`
                          : '—')}
                    </td>
                    <td><strong>{o.internalOrderId || '—'}</strong></td>
                    <td><code style={{ opacity: 0.85 }}>{o.orderId ?? '—'}</code></td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan="11" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)' }}>
                      No matching orders.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 12 }}>
            {ordersTotal > 0
              ? `Page ${ordersPage} of ${ordersTotalPages} — ${ordersTotal} total order${ordersTotal === 1 ? '' : 's'}`
              : 'No orders'}
          </div>
          {ordersTotalPages > 1 && (
            <div className="admin-smm-pager">
              <button
                className="admin-smm-btn admin-smm-btn--ghost"
                type="button"
                disabled={ordersPage <= 1}
                onClick={() => fetchOrders(Math.max(1, ordersPage - 1))}
              >
                Prev
              </button>
              <div className="admin-smm-pagernumbers">
                {getPageItems(ordersPage, ordersTotalPages).map((item) => {
                  if (typeof item !== 'number') {
                    return (
                      <span key={item} className="admin-smm-ellipsis">…</span>
                    );
                  }
                  const isActive = item === ordersPage;
                  return (
                    <button
                      key={item}
                      className={`admin-smm-pagebtn ${isActive ? 'active' : ''}`}
                      type="button"
                      disabled={isActive}
                      onClick={() => fetchOrders(item)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
              <button
                className="admin-smm-btn admin-smm-btn--ghost"
                type="button"
                disabled={ordersPage >= ordersTotalPages}
                onClick={() => fetchOrders(Math.min(ordersTotalPages, ordersPage + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSMMManager;

